import React from 'react'
import { Loader2, Search as SearchIcon } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { listContactsCore, listContactsForGroupCore } from '../lib/contactsCore'

export type SpeakerAssignProps = {
    speakerLabels: string[]
    value: Record<string, { contactId: string | null; displayName: string }>
    onChange: (map: Record<string, { contactId: string | null; displayName: string }>) => void
    groupId?: string | null
}

export default function SpeakerAssign({ speakerLabels, value, onChange, groupId }: SpeakerAssignProps) {
    const [openIdx, setOpenIdx] = React.useState<number | null>(null)
    const [query, setQuery] = React.useState('')
    const [loading, setLoading] = React.useState(false)
    const [results, setResults] = React.useState<any[]>([])

    React.useEffect(() => {
        if (openIdx == null) return
        const q = query.trim()
        if (!q) { setResults([]); return }
        const t = setTimeout(async () => {
            try {
                setLoading(true)
                const rows = groupId
                    ? await listContactsForGroupCore(supabase as any, groupId, { query: q, includeArchived: false as const, limit: 6, offset: 0 })
                    : await listContactsCore(supabase as any, { query: q, includeArchived: false as const, limit: 6, offset: 0 })
                setResults(rows)
            } catch { setResults([]) } finally { setLoading(false) }
        }, 200)
        return () => clearTimeout(t)
    }, [query, openIdx, groupId])

    const setSpeaker = (idx: number, contact: any | null) => {
        const label = speakerLabels[idx]
        const next = { ...value }
        if (contact) {
            next[label] = { contactId: contact.id, displayName: `${contact.first_name || ''} ${contact.last_name || ''}`.trim() || 'Unnamed' }
        } else {
            next[label] = { contactId: null, displayName: `Speaker ${idx + 1}` }
        }
        onChange(next)
    }

    return (
        <div>
            <div className="text-xs text-gray-400 mb-2">Speakers</div>
            <div className="flex flex-wrap gap-2">
                {speakerLabels.map((lbl, idx) => {
                    const current = value[lbl]
                    return (
                        <div key={lbl + idx} className="relative">
                            <button
                                type="button"
                                onClick={() => setOpenIdx(i => i === idx ? null : idx)}
                                className="inline-flex items-center gap-2 px-2.5 py-1.5 rounded-full border border-gray-700 bg-gray-850 text-gray-200 hover:bg-gray-800"
                                title={`Assign ${lbl}`}
                            >
                                <div className="w-5 h-5 rounded-full bg-gray-700 overflow-hidden">
                                    {current?.contactId && (current as any).avatar ? (
                                        <img src={(current as any).avatar} className="w-full h-full object-cover" />
                                    ) : null}
                                </div>
                                <span className="text-xs">{current?.displayName || lbl}</span>
                            </button>
                            {openIdx === idx && (
                                <div className="absolute z-50 mt-2 w-64 bg-gray-900 border border-gray-800 rounded-lg shadow-xl p-2">
                                    <div className="relative">
                                        <SearchIcon className="w-4 h-4 text-gray-500 absolute left-2 top-1/2 -translate-y-1/2" />
                                        <input
                                            autoFocus
                                            value={query}
                                            onChange={(e) => setQuery(e.target.value)}
                                            placeholder="Search…"
                                            className="w-full pl-7 pr-2 py-1.5 bg-gray-850 border border-gray-800 rounded-md text-gray-200 placeholder-gray-500 text-sm"
                                        />
                                    </div>
                                    <div className="mt-2 max-h-56 overflow-auto">
                                        {loading && <div className="py-2 text-center text-gray-400 text-sm"><Loader2 className="w-4 h-4 animate-spin inline" /> Loading…</div>}
                                        {!loading && results.length === 0 && (
                                            <div className="py-2 text-center text-gray-500 text-sm">No matches</div>
                                        )}
                                        {results.map(r => (
                                            <button
                                                key={r.id}
                                                onClick={() => { setSpeaker(idx, r); setOpenIdx(null); setQuery(''); setResults([]) }}
                                                className="w-full flex items-center gap-2 px-2 py-1 rounded-md hover:bg-gray-800 text-left"
                                            >
                                                <div className="w-7 h-7 rounded-full bg-gray-700 overflow-hidden">
                                                    {r.profile_image_url ? <img src={r.profile_image_url} className="w-full h-full object-cover" /> : null}
                                                </div>
                                                <div className="min-w-0">
                                                    <div className="text-xs text-gray-200 truncate">{`${r.first_name || ''} ${r.last_name || ''}`.trim() || 'Unnamed'}</div>
                                                    {r.company && <div className="text-[10px] text-gray-500 truncate">{r.company}</div>}
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                    <div className="mt-2 flex items-center justify-end gap-2">
                                        <button onClick={() => { setSpeaker(idx, null); setOpenIdx(null) }} className="px-2 py-1 text-xs rounded-md border border-gray-700 text-gray-300 hover:bg-gray-800">Clear</button>
                                        <button onClick={() => setOpenIdx(null)} className="px-2 py-1 text-xs rounded-md border border-blue-800 bg-blue-700 text-white hover:bg-blue-600">Done</button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )
                })}
            </div>
        </div>
    )
}
